import { z } from "zod";
import { ResultItemSchema } from "../../../base/submitResults.schema";
import {
    ChartSubmitArtifactParamsSchema,
    RangeSubmitArtifactParamsSchema,
} from "../submitArtifact/submitArtifact.schema";

/**
 * Optional artifact export request that can be bundled with submitResults.
 * Uses the same export fields as submitArtifact plus requirement routing.
 */
const ArtifactCreationRequestBaseSchema = z.object({
    requirementId: z.string().optional().describe(
        "Requirement to attach this artifact to. Required when multiple requirements are submitted."
    ),
});

export const ArtifactCreationRequestSchema = z.discriminatedUnion("artifact_type", [
    ArtifactCreationRequestBaseSchema.merge(ChartSubmitArtifactParamsSchema),
    ArtifactCreationRequestBaseSchema.merge(RangeSubmitArtifactParamsSchema),
]);

/**
 * LinkedExcelEditor-specific submit payload.
 * - results/summary/notes match generic submitResults
 * - artifactsToCreate adds optional inline artifact export behavior
 */
export const LinkedExcelSubmitResultsParamsSchema = z.object({
    results: z.array(ResultItemSchema).describe("Final results, one per requirement"),
    summary: z.string().optional().describe("Optional summary"),
    notes: z.string().optional().describe("Optional notes/warnings"),
    artifactsToCreate: z.array(ArtifactCreationRequestSchema).optional().describe(
        "Optional artifacts to export before submission"
    ),
}).superRefine((value, ctx) => {
    if (!value.artifactsToCreate || value.artifactsToCreate.length === 0) return;
    if (value.results.length <= 1) return;

    for (let i = 0; i < value.artifactsToCreate.length; i++) {
        const item = value.artifactsToCreate[i];
        if (!item.requirementId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["artifactsToCreate", i, "requirementId"],
                message: "requirementId is required when submitting artifacts for multiple requirements.",
            });
        }
    }
});

export type ArtifactCreationRequest = z.infer<typeof ArtifactCreationRequestSchema>;
export type LinkedExcelSubmitResultsParams = z.infer<typeof LinkedExcelSubmitResultsParamsSchema>;
